import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useActivateContract,
  useCreateContractDraft,
  usePreviewSchedule,
  type Frequency,
  type PreviewScheduleRow,
} from '../api/contracts';
import { useCustomersList } from '@/features/customers/api/customers';
import { useProjectPricingPlans, type PricingPlan } from '@/features/pricing-plans/api/pricing-plans';
import { computePlanTotals } from '../lib/tariff-calc';
import { api, asApiError } from '@/shared/lib/api';
import { formatUzs } from '@/shared/lib/format';

type AptForSelect = {
  id: string;
  fullCode: string;
  basePrice: string;
  pricePerSqm: string;
  areaSqm: string;
  projectId: string;
  status: string;
};

export function ContractCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const createDraft = useCreateContractDraft();
  const activate = useActivateContract();
  const previewMut = usePreviewSchedule();
  const [search, setSearch] = useState('');
  const { data: customers, isLoading: customersLoading } = useCustomersList({
    search: search.length >= 2 ? search : undefined,
  });
  const [apartments, setApartments] = useState<AptForSelect[]>([]);
  const [preview, setPreview] = useState<PreviewScheduleRow[] | null>(null);

  const selectedApartmentId = Form.useWatch('apartmentId', form);
  const selectedApartment = useMemo(
    () => apartments.find((a) => a.id === selectedApartmentId) ?? null,
    [apartments, selectedApartmentId],
  );
  const { data: plans } = useProjectPricingPlans(selectedApartment?.projectId);
  const selectedPlanId = Form.useWatch('pricingPlanId', form);
  const selectedPlan = useMemo(
    () => plans?.find((p) => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );
  const discountType = Form.useWatch('discountType', form) as string | undefined;
  const discountValue = Form.useWatch('discountValue', form) as number | undefined;
  const downPaymentValue = Form.useWatch('downPayment', form) as number | undefined;

  // When the plan/apartment changes, auto-fill the down payment + cadence fields.
  // The total is owned by the recompute effect below (it also re-runs on down /
  // discount changes, which the annual_financed surcharge depends on).
  useEffect(() => {
    if (!selectedApartment || !selectedPlan) return;
    const { downTiyin } = computePlanTotals({
      pricePerSqmTiyin: BigInt(selectedApartment.pricePerSqm),
      areaSqm: Number(selectedApartment.areaSqm),
      plan: selectedPlan,
    });
    const isCash = selectedPlan.termMonths === 0;
    const installmentCount = isCash
      ? 1
      : selectedPlan.frequency === 'quarterly'
        ? selectedPlan.termMonths / 3
        : selectedPlan.termMonths;
    const firstDueDate = isCash ? dayjs() : dayjs().add(1, 'month').startOf('month');
    form.setFieldsValue({
      downPayment: isCash ? 0 : Number(downTiyin / 100n),
      installmentCount,
      frequency: isCash ? 'monthly' : selectedPlan.frequency,
      firstDueDate,
    });
  }, [selectedApartment, selectedPlan, form]);

  // Recompute the total whenever the apartment, plan, down payment or discount
  // changes. For annual_financed plans the surcharge is a function of the down
  // payment, so editing the down here flows through to the monthly amount.
  useEffect(() => {
    if (!selectedApartment || !selectedPlan) return;
    const downOverrideTiyin =
      downPaymentValue != null ? BigInt(Math.round(Number(downPaymentValue) * 100)) : undefined;
    const common = {
      pricePerSqmTiyin: BigInt(selectedApartment.pricePerSqm),
      areaSqm: Number(selectedApartment.areaSqm),
      plan: selectedPlan,
      downOverrideTiyin,
    };
    // Discount comes off the cost price (tannarx = base); for annual plans it also
    // lowers the effective price, so the surcharge is recomputed via the helper.
    const baseTiyin = aptBaseTiyin(selectedApartment);
    const discTiyin = calcDiscountTiyin(discountType, discountValue, baseTiyin, selectedApartment);
    const finalTiyin = computePlanTotals({ ...common, discountTiyin: discTiyin }).totalTiyin;
    if (finalTiyin > 0n) form.setFieldValue('totalAmount', Number(finalTiyin / 100n));
  }, [selectedApartment, selectedPlan, discountType, discountValue, downPaymentValue, form]);

  async function loadAvailableApartments(): Promise<AptForSelect[]> {
    try {
      const res = await api.get<{ data: AptForSelect[] }>('/apartments', {
        params: { status: 'available', limit: 200 },
      });
      // Also load reserved (so we can sign contract from a reservation).
      const res2 = await api.get<{ data: AptForSelect[] }>('/apartments', {
        params: { status: 'reserved', limit: 200 },
      });
      const list = [...res.data.data, ...res2.data.data];
      setApartments(list);
      return list;
    } catch (err) {
      void message.error(asApiError(err).message);
      return [];
    }
  }

  // Preselect apartment when arriving from the apartment drawer (?apartmentId=…).
  useEffect(() => {
    const presetApt = searchParams.get('apartmentId');
    if (!presetApt) return;
    void loadAvailableApartments().then((list) => {
      if (list.some((a) => a.id === presetApt)) {
        form.setFieldValue('apartmentId', presetApt);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handlePreview() {
    try {
      const v = await form.validateFields([
        'totalAmount',
        'downPayment',
        'installmentCount',
        'frequency',
        'firstDueDate',
      ]);
      const rows = await previewMut.mutateAsync({
        totalAmount: String(BigInt(Math.round(Number(v.totalAmount) * 100))),
        downPayment: String(BigInt(Math.round(Number(v.downPayment) * 100))),
        installmentCount: Number(v.installmentCount),
        frequency: v.frequency,
        firstDueDate: v.firstDueDate.format('YYYY-MM-DD'),
      });
      setPreview(rows);
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function handleCreateAndActivate() {
    try {
      const v = await form.validateFields();
      const finalTiyin = BigInt(Math.round(Number(v.totalAmount) * 100));
      // The recorded discount is the amount taken off the cost price (tannarx), not the
      // gross−final gap — for annual plans that gap also contains the surcharge reduction.
      const discTiyin = selectedApartment
        ? calcDiscountTiyin(v.discountType, v.discountValue, aptBaseTiyin(selectedApartment), selectedApartment)
        : 0n;
      const draft = await createDraft.mutateAsync({
        apartmentId: v.apartmentId,
        customerId: v.customerId,
        totalAmount: String(finalTiyin),
        downPayment: String(BigInt(Math.round(Number(v.downPayment) * 100))),
        installmentCount: Number(v.installmentCount),
        frequency: v.frequency as Frequency,
        firstDueDate: v.firstDueDate.format('YYYY-MM-DD'),
        lateFeePercentMonthly: Number(v.lateFeePercentMonthly ?? 1),
        gracePeriodDays: Number(v.gracePeriodDays ?? 3),
        language: v.language ?? 'ru',
        signedDate: v.signedDate ? v.signedDate.format('YYYY-MM-DD') : undefined,
        notes: v.notes || undefined,
        discountAmount: discTiyin > 0n ? String(discTiyin) : undefined,
        discountReason: v.discountReason || undefined,
      });
      const activated = await activate.mutateAsync(draft.id);
      void message.success(t('contracts.activated', { number: activated.contractNumber }));
      navigate(`/contracts/${activated.id}`);
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/contracts')}>
          {t('contracts.title')}
        </Button>
        <Typography.Title level={2} style={{ margin: '8px 0 0' }}>
          {t('contracts.newTitle')}
        </Typography.Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            frequency: 'monthly',
            installmentCount: 12,
            lateFeePercentMonthly: 1,
            gracePeriodDays: 3,
            language: 'ru',
            downPayment: 0,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="customerId" label={t('contracts.customer')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder={t('apartments.customerPlaceholder')}
                  filterOption={false}
                  onSearch={setSearch}
                  loading={customersLoading}
                  options={(customers?.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.lastName} ${c.firstName} · ${c.phone}`,
                  }))}
                  notFoundContent={
                    search.length < 2
                      ? t('apartments.customerTypeHint')
                      : t('apartments.customerNotFound')
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="apartmentId" label={t('contracts.apartment')} rules={[{ required: true }]}>
                <Select
                  placeholder="—"
                  onFocus={loadAvailableApartments}
                  options={apartments.map((a) => ({
                    value: a.id,
                    label: `${a.fullCode} (${a.status})`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={24}>
              <Form.Item
                name="pricingPlanId"
                label={t('contracts.pricingPlan')}
                extra={
                  selectedApartment ? t('contracts.pricingPlanHint') : t('contracts.pricingPlanPickApartmentFirst')
                }
              >
                <Select
                  allowClear
                  placeholder={selectedApartment ? '—' : t('contracts.pricingPlanPickApartmentFirst')}
                  disabled={!selectedApartment}
                  suffixIcon={<ThunderboltOutlined />}
                  options={(plans ?? []).map((p) => ({
                    value: p.id,
                    label: planLabel(p, t),
                  }))}
                />
              </Form.Item>
              {selectedPlan && selectedApartment && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: -8, marginBottom: 16 }}
                  message={planSummary(selectedApartment, selectedPlan, t)}
                />
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="discountType" label={t('contracts.discountType')} initialValue="none">
                <Select
                  options={[
                    { value: 'none', label: '—' },
                    { value: 'percent', label: t('contracts.discountTypes.percent') },
                    { value: 'fixed', label: t('contracts.discountTypes.fixed') },
                    { value: 'per_sqm', label: t('contracts.discountTypes.per_sqm') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="discountValue" label={t('contracts.discountValue')}>
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={0}
                  disabled={!discountType || discountType === 'none'}
                  formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                  addonAfter={discountType === 'percent' ? '%' : 'UZS'}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="discountReason" label={t('contracts.discountReason')}>
                <Input maxLength={255} disabled={!discountType || discountType === 'none'} />
              </Form.Item>
            </Col>
          </Row>
          {discountType && discountType !== 'none' && discountValue && selectedApartment && (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: -8, marginBottom: 16 }}
              message={`${t('contracts.discountPreview')}: −${formatUzs(
                calcDiscountTiyin(discountType, discountValue, aptBaseTiyin(selectedApartment), selectedApartment)
              )}`}
            />
          )}

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="totalAmount"
                label={t('contracts.totalAmount')}
                rules={[{ required: true }]}
              >
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={1}
                  step={1_000_000}
                  formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                  addonAfter="UZS"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="downPayment"
                label={t('contracts.downPayment')}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value == null || value < getFieldValue('totalAmount')) return Promise.resolve();
                      return Promise.reject(new Error(t('contracts.downPaymentExceedsTotal')));
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={0}
                  step={1_000_000}
                  formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                  addonAfter="UZS"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="installmentCount"
                label={t('contracts.installmentCount')}
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={360} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="frequency" label={t('contracts.frequency')}>
                <Select
                  options={[
                    { value: 'monthly', label: t('contracts.frequencyMonthly') },
                    { value: 'quarterly', label: t('contracts.frequencyQuarterly') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="firstDueDate"
                label={t('contracts.firstDueDate')}
                rules={[{ required: true }]}
                initialValue={dayjs()}
                tooltip={t('contracts.firstDueDateHint')}
                extra={t('contracts.firstDueDateHint')}
              >
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="lateFeePercentMonthly" label={t('contracts.lateFeePercentMonthly')}>
                <InputNumber<number> style={{ width: '100%' }} min={0} step={0.1} addonAfter="%/mo" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="gracePeriodDays" label={t('contracts.gracePeriodDays')}>
                <InputNumber style={{ width: '100%' }} min={0} max={60} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="language" label={t('contracts.language')}>
                <Select
                  options={[
                    { value: 'ru', label: 'Русский' },
                    { value: 'uz', label: "O'zbek" },
                    { value: 'en', label: 'English' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="signedDate" label={t('contracts.signedDate')}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="notes" label={t('contracts.notes')}>
                <Input.TextArea rows={1} maxLength={2000} />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button onClick={handlePreview} loading={previewMut.isPending}>
              {t('contracts.previewSchedule')}
            </Button>
            <Button
              type="primary"
              onClick={handleCreateAndActivate}
              loading={createDraft.isPending || activate.isPending}
            >
              {t('contracts.createAndActivate')}
            </Button>
          </Space>
        </Form>
      </Card>

      {preview && (
        <Card title={t('contracts.schedulePreview')}>
          <Table
            rowKey="installmentNumber"
            dataSource={preview}
            pagination={false}
            size="small"
            columns={[
              { title: '#', dataIndex: 'installmentNumber', width: 60 },
              {
                title: t('contracts.dueDate'),
                dataIndex: 'dueDate',
                render: (v: string) => dayjs(v).format('DD.MM.YYYY'),
              },
              {
                title: t('contracts.amountDue'),
                dataIndex: 'amountDue',
                render: (v: string) => formatUzs(v),
              },
              {
                title: t('contracts.type'),
                dataIndex: 'isDownPayment',
                render: (v: boolean) =>
                  v ? t('contracts.downPaymentRow') : t('contracts.installment'),
              },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

// Cost price (tannarx) in tiyin = pricePerSqm × area. Discounts are taken off this.
function aptBaseTiyin(apt: AptForSelect): bigint {
  return (BigInt(apt.pricePerSqm) * BigInt(Math.round(Number(apt.areaSqm) * 100))) / 100n;
}

function calcDiscountTiyin(
  type: string | undefined,
  value: number | undefined,
  baseTotal: bigint,
  apt: AptForSelect | null,
): bigint {
  if (!type || type === 'none' || !value || value <= 0) return 0n;
  if (type === 'percent') {
    return (baseTotal * BigInt(Math.round(value * 100))) / 10_000n;
  }
  if (type === 'fixed') {
    return BigInt(Math.round(value * 100));
  }
  if (type === 'per_sqm' && apt) {
    const areaCentisqm = BigInt(Math.round(Number(apt.areaSqm) * 100));
    return (BigInt(Math.round(value * 100)) * areaCentisqm) / 100n;
  }
  return 0n;
}

function planLabel(p: PricingPlan, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (p.termMonths === 0) return `${p.name} · ${t('pricingPlans.cash')}`;
  const term = `${p.termMonths} ${t('pricingPlans.monthsAbbr')}`;
  const down = `${Number(p.downPaymentPercent)}% ${t('contracts.downPayment').toLowerCase()}`;
  return `${p.name} · ${term} · ${down}`;
}

function planSummary(
  apt: AptForSelect,
  plan: PricingPlan,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const pricePerSqmTiyin = BigInt(apt.pricePerSqm);
  const area = Number(apt.areaSqm);
  const { totalTiyin, surchargeTiyin } = computePlanTotals({
    pricePerSqmTiyin,
    areaSqm: area,
    plan,
  });
  if (plan.surchargeMode === 'annual_financed') {
    return t('contracts.planComputedAnnual', {
      ratePerSqmYear: formatUzs(plan.surchargePerSqm),
      years: plan.termMonths / 12,
      area,
      surcharge: formatUzs(surchargeTiyin),
      total: formatUzs(totalTiyin),
    });
  }
  return t('contracts.planComputed', {
    perSqm: formatUzs(pricePerSqmTiyin + BigInt(plan.surchargePerSqm)),
    area,
    total: formatUzs(totalTiyin),
  });
}
